import type { Dispatch, SetStateAction } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CrossIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { getLocalStorage, setLocalStorage } from '@/utils/localstorage';

import { AccountSignin } from './components/account-signin';
import { Input } from './components/input';
import { Social } from './components/social';
import { Submit } from './components/submit';

// CSS
const signinPromptDropdownMenuStyles = css({
	['@media (max-width: 768px)']: {
		display: 'none',
	},
	display: 'block',
	position: 'absolute',
	insetBlockStart: '100%',
	insetInlineEnd: 0,
	padding: token('space.300'),
	borderRadius: token('space.100'),
	backgroundColor: wacToken('color.neutral.light0'),
	marginBlockStart: token('space.400'),
	width: '350px',
	maxWidth: 'min(350px, calc(100vw - 32px))',
	boxShadow: token('elevation.shadow.overlay'),
	zIndex: 19,
	['@keyframes fadeInFromTop']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		from: {
			opacity: 0,
			transform: 'translate(0, -50px)',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		to: {
			opacity: 1,
			transform: 'translate(0, 0)',
		},
	},
	animation: 'fadeInFromTop 0.8s ease-out forwards',
});

const closeIconStyles = css({
	alignItems: 'center',
	height: token('space.300'),
	background: 'none',
	border: 'none',
	display: 'flex',
	float: 'inline-end',
	flexDirection: 'column',
	position: 'relative',
	'&:hover': {
		cursor: 'pointer',
	},
	'&:focus': {
		border: `1px ${wacToken('color.neutral.dark0')} solid`,
	},
	tabindex: 0,
	width: token('space.300'),
});

const headerTextStyles = css({
	maxWidth: '436px',
	overflowWrap: 'break-word',
	textAlign: 'left',
	display: 'block',
	color: wacToken('color.primary.black'),
});

const headerStyles = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: token('space.250'),
	fontWeight: 700,
	lineHeight: '28px',
	margin: token('space.0'),
});

const subHeaderStyles = css({
	color: token('color.text.subtle'),
	fontFamily: 'Charlie Text',
	fontSize: '16px',
	lineHeight: '24px',
	fontStyle: 'normal',
	fontWeight: 400,
});

const closeIconWrapperStyles = css({
	position: 'absolute',
	insetInlineEnd: token('space.300'),
	insetBlockStart: token('space.300'),
});

const darkGrayTextCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"div[role='note']": {
		// eslint-disable-next-line local/ensure-wac-token-usage
		color: '#44546F',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"div[role='alert']": {
		color: `${token('color.icon.accent.red')} !important`,
	},
});

const accountCreationContainerStyles = css({
	paddingBlockStart: token('space.200'),
});

// Components

export const GeneralSigninPromptMenu = ({
	setIsUseGeneralSigninPromptConfig,
}: {
	setIsUseGeneralSigninPromptConfig?: Dispatch<SetStateAction<boolean>>;
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleCloseButton = () => {
		const consecutiveXClicksItem = 'general_signin_prompt_x_clicks';
		const lastVisitTimestampItem = 'general_signin_prompt_last_visit_timestamp';
		const currentXClicks = Number(getLocalStorage(consecutiveXClicksItem, true) || '0');
		setLocalStorage(consecutiveXClicksItem, String(currentXClicks + 1));
		setLocalStorage(lastVisitTimestampItem, new Date().toISOString());
		createAnalyticsEvent({
			eventName: 'gwp.generalSigninPrompt.dismissed',
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'linkButton',
			actionSubjectId: 'GeneralSigninPromptCloseIcon',
		}).fire();
		setIsUseGeneralSigninPromptConfig?.(false);
	};

	return (
		<div
			css={signinPromptDropdownMenuStyles}
			className="general-signin-prompt-dropdown-menu"
			data-testid="genearl-signin-prompt-dropdown-menu"
			aria-label="global-nav-signin-prompt-dropdown-menu"
		>
			<div css={closeIconWrapperStyles}>
				<button
					css={closeIconStyles}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						handleCloseButton();
					}}
					aria-label="general-signin-prompt-modal-dismissal-button"
				>
					<CrossIcon label={'close'} size={'medium'} />
				</button>
			</div>
			<div css={headerTextStyles}>
				<h5 css={headerStyles}>
					<FormattedMessage
						id="genearl-signin-prompt.header.title"
						defaultMessage={"You're logged out."}
					/>
				</h5>
				<div css={subHeaderStyles}>
					<FormattedMessage
						id="genearl-signin-prompt.header.subtext"
						defaultMessage={'Log in to your account for a more personalized experience.'}
					/>
				</div>
			</div>
			<div css={accountCreationContainerStyles}>
				<AccountSignin
					Input={
						<div css={darkGrayTextCss}>
							<Input
								labelText={intl.formatMessage({
									id: 'genearl-signin-prompt.email-label',
									defaultMessage: 'Email',
								})}
								helpText={''}
							/>
						</div>
					}
					Login={null}
					Social={
						<div className="general-signin-prompt-social-wrapper">
							<Social
								providers={{
									google: 'Google',
									microsoft: 'Microsoft',
								}}
							/>
						</div>
					}
					Submit={
						<div className="general-signin-prompt-submit-wrapper">
							<Submit
								buttonText={intl.formatMessage({
									id: 'genearl-signin-prompt.login-button',
									defaultMessage: 'Log in',
								})}
							/>
						</div>
					}
				/>
			</div>
		</div>
	);
};

export const GeneralSigninPrompt = ({
	isUseGeneralSigninPromptConfig = false,
	setIsUseGeneralSigninPromptConfig,
}: {
	isUseGeneralSigninPromptConfig?: boolean;
	setIsUseGeneralSigninPromptConfig?: Dispatch<SetStateAction<boolean>>;
}) => {
	return (
		isUseGeneralSigninPromptConfig && (
			<GeneralSigninPromptMenu
				setIsUseGeneralSigninPromptConfig={setIsUseGeneralSigninPromptConfig}
			/>
		)
	);
};

GeneralSigninPrompt.displayName = 'GeneralSigninPrompt';

export default GeneralSigninPrompt;
